import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit';

export type ThemeValues = 'auto' | 'light' | 'dark';

// Local storage key for persistence
const localStorageKey = 'uiState';

type State = {
  sidebarShow: boolean;
  sidebarUnfoldable: boolean;
  theme: ThemeValues;
};

// Load initial state from localStorage
const loadState = (): State | undefined => {
  try {
    const storedState = localStorage?.getItem(localStorageKey);
    return storedState ? JSON.parse(storedState) : undefined;
  } catch {
    return undefined;
  }
};

// Define initial state
const initialState: State = loadState() || {
  sidebarShow: true,
  sidebarUnfoldable: false,
  theme: 'auto',
};

// Create a slice for state management
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeValues>) => {
      state.theme = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarShow = !state.sidebarShow;
    },
    setSidebarShow: (state, action: PayloadAction<boolean>) => {
      state.sidebarShow = action.payload;
    },
    toggleSidebarUnfoldable: (state) => {
      state.sidebarUnfoldable = !state.sidebarUnfoldable;
    },
  },
});

// Export actions for use in components
export const { toggleSidebar, setSidebarShow, toggleSidebarUnfoldable, setTheme } = uiSlice.actions;

// **Enhance the reducer to persist state**
const enhancedReducer: typeof uiSlice.reducer = (state: State | undefined, action: UnknownAction) => {
  const newState = uiSlice.reducer(state, action);

  if (action.type?.startsWith(uiSlice.name + '/')) {
    localStorage?.setItem(localStorageKey, JSON.stringify(newState));
  }

  return newState;
};

export default enhancedReducer;
